import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseError } from '@/types/Error';
import { ProductReviewRequest } from '@/types/Product';
import {
  ProductReview,
  ProductReviewSummary,
  TrustpilotInvitationLinkPayload,
  TrustpilotInvitationResponse,
} from '@/types/ProductReview';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

const contentTypes = {
  applicationJson: 'application/json',
};

export const productReviewsApi = createApi({
  reducerPath: 'productReviewsApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `ProductReview` }),
  tagTypes: ['ProductReviews'],
  endpoints: builder => ({
    getProductReviews: builder.query<
      ProductReview[],
      {
        productReviewRequest: ProductReviewRequest[] | undefined;
        page: number;
        pageSize: number;
        sort: string;
        includeReviews: boolean;
      }
    >({
      query: ({ productReviewRequest, page, pageSize, sort, includeReviews }) => {
        const params = new URLSearchParams();

        if (page !== undefined) {
          params.append('page', page.toString());
        }
        if (pageSize !== undefined) {
          params.append('pageSize', pageSize.toString());
        }
        if (sort) {
          params.append('sort', sort);
        }
        params.append('includeReviews', includeReviews ? 'true' : 'false');

        return {
          url: `/GetProductReviews?${params.toString()}`,
          method: 'POST',
          body: productReviewRequest,
        };
      },
      transformResponse: (response: ProductReview[], meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['ProductReviews'],
    }),
    getProductReviewInvitationLink: builder.mutation<TrustpilotInvitationResponse, TrustpilotInvitationLinkPayload>({
      query: data => ({
        url: '/GetInvitationLink',
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: TrustpilotInvitationResponse, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getProductReviewSummary: builder.query<ProductReviewSummary, { productSKU: string }>({
      query: ({ productSKU }) => {
        const params = new URLSearchParams({
          productSKU,
        });

        return {
          url: `/GetReviewSummary?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: ProductReviewSummary, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['ProductReviews'],
    }),
  }),
});

export const { useGetProductReviewsQuery, useGetProductReviewInvitationLinkMutation, useGetProductReviewSummaryQuery } =
  productReviewsApi;
